import React, { createContext, useState, useEffect } from "react";
import { ShowToast, Severty } from "../helper/toast";
import { walletAPI } from "../services/api";

export const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  // Wallet data state
  const [walletSummary, setWalletSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [recentRecipients, setRecentRecipients] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);

  // Loading and pagination state
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Filters for transactions
  const [filters, setFilters] = useState({
    type: "all", // all, credit, debit, etc.
    dateRange: "all", // all, today, week, month, etc.
    searchQuery: "",
  });

  //   load wallet summary
  const loadWalletSummary = async () => {
    setLoading(true);
    try {
      const response = await walletAPI.getWalletSummary();
      localStorage.setItem("walletSummary", JSON.stringify(response.data));
      if (response.status === 200) {
        setWalletSummary(response.data);
        return { success: true, data: response.data };
      } else {
        ShowToast(
          response.message || "Failed to load wallet summary",
          Severty.ERROR
        );
        return {
          success: false,
          error: response.message || "Failed to load wallet summary",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load wallet summary. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loadTokenBundles = async () => {
    try {
      const response = await walletAPI.getTokenBundles();
      if (response.status === 200) {
        return { success: true, data: response.data };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load token bundles. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    }
  };

  const addPayIn = async (data) => {
    try {
      const response = await walletAPI.addPayIn(data);
      if (response.status === 200) {
        return { success: true, data: response.data };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to sent payin request. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    }
  };

  const sendToken = async (data) => {
    try {
      const response = await walletAPI.sendToken(data);
      if (response.status === 200 || response.status === 201) {
        return { success: true, data: response.data };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to sent payin request. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    }
  };

  const addBankDetails = async (data) => {
    try {
      const response = await walletAPI.addBankDetails(data);
      if (response.status === 200 || response.status === 201) {
        return { success: true, data: response.data };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to sent payin request. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    }
  };

  const deleteBankDetails = async (bankId) => {
    try {
      const response = await walletAPI.deleteBankDetails(bankId);
      if (response.status === 200 || response.status === 201) {
        return { success: true, data: response.data };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to delete bank details. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    }
  };

  const makePrimaryBank = async (data) => {
    try {
      const response = await walletAPI.makePrimaryBank(data);
      if (response.status === 200 || response.status === 201) {
        return { success: true, data: response.data };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to making primary bank. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    }
  };

  const withdrawAmount = async (data) => {
    try {
      const response = await walletAPI.withdrawAmount(data);
      if (response.status === 200 || response.status === 201) {
        return { success: true, data: response.data };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to withdraw amount request. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    }
  };
  //   load transactions
  const loadTransactions = async (params) => {
    setLoading(true);
    try {
      const response = await walletAPI.getWalletTransactions(params);
      if (response.status === 200) {
        setTransactions(response.data.items || response.data);
        setLoading(false);
        return { success: true, data: response.data };
      } else {
        ShowToast(
          response.message || "Failed to load transactions",
          Severty.ERROR
        );
        return {
          success: false,
          error: response.message || "Failed to load transactions",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load transactions. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loadRecentRecipients = async () => {
    setLoading(true);
    try {
      const response = await walletAPI.getRecentRecipients();
      if (response.status === 200) {
        setRecentRecipients(response.data.recipients || response.data);
        return { success: true, data: response.data };
      } else {
        ShowToast(
          response.message || "Failed to load recent recipients",
          Severty.ERROR
        );
        return {
          success: false,
          error: response.message || "Failed to load recent recipients",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load recent recipients. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const loadBankDetails = async () => {
    setLoading(true);
    try {
      const response = await walletAPI.getBankDetails();
      if (response.status === 200) {
        setBankDetails(response.data);
        return { success: true, data: response.data };
      } else {
        ShowToast(
          response.message || "Failed to load bank details",
          Severty.ERROR
        );
        return {
          success: false,
          error: response.message || "Failed to load bank details",
        };
      }
    } catch (error) {
      const errorMessage =
        error.message ||
        error.error ||
        "Failed to load bank details. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const refreshWalletData = async () => {
    const summaryPromise = loadWalletSummary();
    const transactionsPromise = loadTransactions();
    const recipientsPromise = loadRecentRecipients();
    const bankDetailsPromise = loadBankDetails();

    try {
      await Promise.all([
        summaryPromise,
        transactionsPromise,
        recipientsPromise,
        bankDetailsPromise,
      ]);
      ShowToast("Wallet data refreshed successfully!", Severty.SUCCESS);
      return { success: true };
    } catch (error) {
      ShowToast("Failed to refresh wallet data", Severty.ERROR);
      return { success: false, error: "Failed to refresh wallet data" };
    }
  };

  const updateFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const clearWalletData = () => {
    setWalletSummary(null);
    setTransactions([]);
    setRecentRecipients([]);
    setBankDetails(null);
  };

  return (
    <WalletContext.Provider
      value={{
        // State
        walletSummary,
        transactions,
        recentRecipients,
        bankDetails,
        loading,
        filters,
        pagination,

        // Actions
        loadWalletSummary,
        loadTransactions,
        loadRecentRecipients,
        loadBankDetails,
        refreshWalletData,
        updateFilters,
        updatePagination,
        clearWalletData,
        loadTokenBundles,
        addPayIn,
        sendToken,
        addBankDetails,
        withdrawAmount,
        deleteBankDetails,
        makePrimaryBank,

        // Setters
        setWalletSummary,
        setTransactions,
        setRecentRecipients,
        setBankDetails,
        setLoading,
        setFilters,
        setPagination,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};

// Custom hook to use the wallet context
export const useWallet = () => {
  const context = React.useContext(WalletContext);
  if (context === undefined) {
    throw new Error("useWallet must be used within a WalletProvider");
  }
  return context;
};
